import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useParams, useLocation } from "react-router-dom";

const BASE_URL = import.meta?.env?.VITE_BASE_URL || "https://nissi-india.com";

const ProductionOutwardReportPrint = () => {
  const { outwardId: outwardIdParam } = useParams();
  const location = useLocation();

  const query = useMemo(() => new URLSearchParams(location.search), [location.search]);

  // -----------------------------
  // Helpers
  // -----------------------------
  const getLS = (key, fallback = "") => {
    if (typeof window === "undefined") return fallback;
    const val = localStorage.getItem(key);
    return val !== null && val !== undefined && String(val).trim() !== ""
      ? val
      : fallback;
  };

  const toInt = (v, fallback = 0) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : fallback;
  };

  const toStr = (v, fallback = "") => {
    if (v === null || v === undefined) return fallback;
    const s = String(v).trim();
    return s !== "" ? s : fallback;
  };

  const formatNumber = (value) => {
    const n = Number(value);
    return Number.isFinite(n)
      ? n.toLocaleString("en-IN", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      : "0.00";
  };

  const formatDate = (value) => {
    if (!value) return "";
    if (typeof value === "string") {
      if (/^\d{2}\/\d{2}\/\d{4}$/.test(value)) return value;
      if (value.includes("T")) return value.slice(0, 10);
      if (value.length >= 10) return value.slice(0, 10);
      return value;
    }
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "";
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  };

  // -----------------------------
  // localStorage values
  // -----------------------------
  const finYearId = getLS("finYearId");
  const finYear = getLS("finYear");
  const branchIdLS = getLS("branchId") || getLS("branchid");
  const branchTypeLS = getLS("branchType") || getLS("branchtype") || "HO";
  const userIdLS =
    getLS("userId") || getLS("loginuserid") || getLS("loginUserId");

  // -----------------------------
  // parsed values
  // -----------------------------
  const branchid = useMemo(
    () =>
      toInt(
        query.get("branchid") ||
          query.get("branchId") ||
          branchIdLS,
        0
      ),
    [query, branchIdLS]
  );

  const branchtype = useMemo(() => toStr(branchTypeLS, "HO"), [branchTypeLS]);
  const userid = useMemo(() => toInt(userIdLS, 0), [userIdLS]);

  // outwardid from route OR query
  const outwardid = useMemo(
    () =>
      toInt(
        outwardIdParam ||
          query.get("outid") ||
          query.get("outwardId") ||
          query.get("outwardid"),
        0
      ),
    [outwardIdParam, query]
  );

  // -----------------------------
  // common headers
  // -----------------------------
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": toStr(finYearId, ""),
      "x-finyear": toStr(finYear, ""),
      "x-branchid": String(branchid || ""),
      "x-branchtype": branchtype,
      "x-userid": String(userid || ""),
    }),
    [finYearId, finYear, branchid, branchtype, userid]
  );

  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const fetchReportData = async () => {
    if (!branchid) {
      setError("branchid missing (localStorage.branchId/branchid or query ?branchid=)");
      setReportData(null);
      return;
    }

    if (!outwardid) {
      setError("outwardid missing in URL. Use ?outid=xx or route param");
      setReportData(null);
      return;
    }

    try {
      setLoading(true);
      setError("");

      const res = await axios.get(`${BASE_URL}/api/outwardListing/printOne`, {
        params: { branchid, outwardid },
        headers: commonHeaders,
      });

      if (!res.data?.success) {
        setError(res.data?.message || "Failed to load production outward report data");
        setReportData(null);
        return;
      }

      setReportData(res.data.data || null);

      setTimeout(() => {
        window.print();
      }, 400);
    } catch (err) {
      console.error("Print fetch error:", err);
      setError(
        err?.response?.data?.message ||
          "Server error while loading production outward report"
      );
      setReportData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReportData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outwardid, branchid]);

  return (
    <div className="max-w-full mx-auto p-4 bg-white font-sans text-xs print:p-0 print:max-w-none">
      <div className="border-2 border-black">
        {/* Header */}
        <div className="border-b-2 border-black">
          <div className="grid grid-cols-[140px_1fr]">
            <div className="border-r-2 border-black p-3 flex items-center justify-center">
              <div className="text-center">
                <div className="w-24 h-20 bg-black mx-auto flex items-center justify-center mb-1">
                  <div className="text-white text-2xl">📷</div>
                </div>
                <div className="text-[8px] font-bold mt-1">NISSI INFOTECH PVT LTD</div>
              </div>
            </div>

            <div className="text-center py-3 px-4">
              <h1 className="text-lg font-bold">Nissi Infotech</h1>
              <div className="text-[10px] mt-0.5">Nissi</div>
              <div className="text-sm font-bold mt-0.5">CIVIL CONSTRUCTION</div>
              <div className="text-[10px] mt-1">
                3/42-12, Nagappa colony, kavundampalayam--641030, Tamil Nadu,
              </div>
              <div className="text-[10px]">
                Phone No: 9003744644 Email: infraconsoft-implementa
              </div>
            </div>
          </div>
        </div>

        <div className="text-center py-2 border-b-2 border-black font-bold text-base">
          Production Outward Report
        </div>

        {/* Loading */}
        {loading && (
          <div className="text-center py-4">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-black"></div>
            <div className="mt-2">Loading report data...</div>
          </div>
        )}

        {/* Error */}
        {error && !loading && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded m-4">
            <div className="font-bold">Error loading report:</div>
            <div className="mt-1">{error}</div>
            <div className="mt-2 text-xs text-gray-700">
              Debug: branchid={branchid || "-"} | outwardid={outwardid || "-"}
            </div>
            <button
              onClick={fetchReportData}
              className="mt-3 bg-red-600 text-white px-3 py-1 rounded text-xs hover:bg-red-700"
            >
              Retry
            </button>
          </div>
        )}

        {/* Report Details */}
        {!loading && reportData && (
          <>
            <div className="p-4 border-b-2 border-black">
              <div className="grid grid-cols-2 gap-x-12 text-xs">
                <div className="space-y-3">
                  <div className="flex">
                    <span className="font-bold w-36">Outward No:</span>
                    <span>{reportData.fld_outwardno || reportData.outwardno || "0007"}</span>
                  </div>

                  <div className="flex">
                    <span className="font-bold w-36">Project Name:</span>
                    <span>
                      {reportData.fld_projectname || reportData.projectname || "road test"}
                    </span>
                  </div>

                  <div className="flex">
                    <span className="font-bold w-36">Production Code:</span>
                    <span>
                      {reportData.fld_productioncode ||
                        reportData.productioncode ||
                        "2"}
                    </span>
                  </div>

                  <div className="flex">
                    <span className="font-bold w-36">Production Qty:</span>
                    <span>
                      {formatNumber(
                        reportData.fld_productionqty ?? reportData.productionqty ?? 0
                      )}
                    </span>
                  </div>

                  <div className="flex">
                    <span className="font-bold w-36">Balance Qty:</span>
                    <span>
                      {formatNumber(
                        reportData.fld_balanceqty ?? reportData.balanceqty ?? 0
                      )}
                    </span>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex">
                    <span className="font-bold w-36">Date:</span>
                    <span>{formatDate(reportData.fld_date || reportData.date)}</span>
                  </div>

                  <div className="flex">
                    <span className="font-bold w-36">Production Name:</span>
                    <span>
                      {reportData.fld_productionname ||
                        reportData.productionname ||
                        "testing-2"}
                    </span>
                  </div>

                  <div className="flex">
                    <span className="font-bold w-36">Work Type Name:</span>
                    <span>
                      {reportData.fld_worktypename ||
                        reportData.worktypename ||
                        "pavement test work"}
                    </span>
                  </div>

                  <div className="flex">
                    <span className="font-bold w-36">Transfer Qty:</span>
                    <span>
                      {formatNumber(
                        reportData.fld_transferqty ?? reportData.transferqty ?? 0
                      )}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Signature */}
            <div className="border-b-2 border-black">
              <div className="grid grid-cols-3 text-xs">
                <div className="border-r-2 border-black px-4 py-20 text-left">
                  <div className="font-bold">Prepared By</div>
                </div>
                <div className="border-r-2 border-black px-4 py-20 text-center">
                  <div className="font-bold">Checked By</div>
                </div>
                <div className="px-4 py-20 text-right">
                  <div className="font-bold">Approved By</div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="p-3 text-[10px] text-gray-700">
              <div>
                Report generated by ERP-Software - www.infraconsoft.com owned by
                www.nissiinfotech.com
              </div>
            </div>
          </>
        )}
      </div>

      <style>{`
        @media print {
          body { margin: 0; }
          .print\\:hidden { display: none !important; }
          .print\\:p-0 { padding: 0 !important; }
          .print\\:max-w-none { max-width: none !important; }
          * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          @page { margin: 0.5cm; }
        }
      `}</style>
    </div>
  );
};

export default ProductionOutwardReportPrint;